<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import MessageStatus from '../MessageStatus.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { useMessageContext } from '../provider.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  ATTACHMENT_TYPES,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  ORIENTATION,
} from '../constants';

// Aceleres: bolha única que renderiza um álbum de imagens em grade.
// A detecção e o agrupamento acontecem em MessageList.vue; aqui consumimos
// `albumMessages` e mantemos os metadados de cada mensagem na miniatura.

const { albumMessages, orientation, messageType } = useMessageContext();
const PREVIEW_TILE_COUNT = 3;

const albumItems = computed(() => {
  const list = albumMessages?.value || [];
  return list
    .map(message => ({
      message,
      attachment: (message.attachments && message.attachments[0]) || null,
    }))
    .filter(item => item.attachment);
});

// O visor aberto a partir do álbum deve navegar somente pelas imagens desse
// álbum, na mesma ordem em que elas aparecem na conversa. Usar todos os
// anexos do chat misturava imagens de outros momentos da conversa e fazia a
// tira inferior parecer incompleta ou fora de ordem.
const albumGalleryAttachments = computed(() =>
  albumItems.value.map(item => useSnakeCase(item.attachment))
);

const total = computed(() => albumItems.value.length);

// Aceleres: video no album usa o mesmo tile quadrado/coberto da imagem,
// mas a fonte precisa ser o poster (thumbUrl) - dataUrl de video e o
// arquivo de midia em si, nao da pra usar em <img>.
const isVideoAttachment = attachment => {
  const attachmentType = String(
    attachment.fileType ||
      attachment.file_type ||
      attachment.contentType ||
      attachment.content_type ||
      ''
  ).toLowerCase();
  return (
    attachmentType === ATTACHMENT_TYPES.VIDEO ||
    attachmentType.startsWith('video/')
  );
};

const tileSrc = attachment =>
  isVideoAttachment(attachment)
    ? attachment.thumbUrl
    : attachment.dataUrl || attachment.thumbUrl;

const previewTiles = computed(() =>
  albumItems.value.slice(0, PREVIEW_TILE_COUNT + 1)
);
const remainingCount = computed(() =>
  Math.max(0, total.value - (PREVIEW_TILE_COUNT + 1))
);
const showSummaryOverlay = computed(() => remainingCount.value > 0);
const remainingLabel = computed(() => `+${remainingCount.value}`);

const albumBubbleBackgroundClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? '!bg-[#d9fdd3]' : '!bg-white'
);

const albumTileBackgroundClass = 'bg-white';

const readableTime = message => messageTimestamp(message?.createdAt, 'HH:mm');
const showSingleTimestamp = computed(
  () => messageType.value === MESSAGE_TYPES.INCOMING
);
const albumTimestampMessage = computed(
  () => albumItems.value[albumItems.value.length - 1]?.message
);

const statusToShow = message => {
  if (
    message?.messageType !== MESSAGE_TYPES.OUTGOING ||
    message?.private ||
    message?.status === MESSAGE_STATUS.FAILED
  ) {
    return null;
  }

  if (
    [
      MESSAGE_STATUS.SENT,
      MESSAGE_STATUS.DELIVERED,
      MESSAGE_STATUS.READ,
    ].includes(message.status)
  ) {
    return message.status;
  }

  return MESSAGE_STATUS.PROGRESS;
};

// O mosaico mostra quatro fotos: as três primeiras sem sobreposição e a quarta
// como a miniatura de resumo quando ainda existem fotos depois dela.
const gridColsClass = 'grid-cols-2';
const tileClass = index =>
  [
    total.value === 3 && index === 0 ? 'col-span-2' : 'aspect-square',
    index === 0 ? '!rounded-tl-none' : '',
  ];
const tileStyle = index =>
  total.value === 3 && index === 0 ? { aspectRatio: '2 / 1' } : undefined;

const showGallery = ref(false);
const clickedAttachment = ref(null);

const openGalleryAt = item => {
  clickedAttachment.value = item.attachment;
  showGallery.value = true;
};

const galleryAttachment = computed(() =>
  clickedAttachment.value ? useSnakeCase(clickedAttachment.value) : null
);
</script>

<template>
  <BaseBubble
    class="acl-image-album !w-[420px] !max-w-[calc(100vw-48px)] !rounded-tl-none overflow-visible pt-0 px-[5px] pb-[5px]"
    :class="albumBubbleBackgroundClass"
    data-bubble-name="image-album"
    hide-meta
    show-sender-name
  >
    <div
      class="acl-image-album__grid relative grid gap-1 overflow-hidden rounded-tr-lg rounded-br-lg rounded-bl-lg rounded-tl-none"
      :class="gridColsClass"
    >
      <div
        v-for="(item, index) in previewTiles"
        :key="item.attachment.id"
        class="relative cursor-pointer overflow-hidden rounded-md group"
        :class="[tileClass(index), albumTileBackgroundClass]"
        :style="tileStyle(index)"
        @click.stop="openGalleryAt(item)"
      >
        <video
          v-if="isVideoAttachment(item.attachment)"
          class="w-full h-full object-cover skip-context-menu !bg-white"
          :src="item.attachment.dataUrl"
          :poster="tileSrc(item.attachment) || undefined"
          preload="metadata"
          muted
          playsinline
          tabindex="-1"
        />
        <img
          v-else
          class="w-full h-full object-cover skip-context-menu !bg-white"
          :src="tileSrc(item.attachment)"
          loading="lazy"
        />
        <div
          class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-gradient-to-t from-[rgba(0,0,0,0.7)] via-[rgba(0,0,0,0.3)] to-transparent"
        />
        <div
          v-if="isVideoAttachment(item.attachment)"
          class="acl-image-album__play-badge pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
        >
          <span
            class="flex items-center justify-center rounded-full bg-[rgba(0,0,0,0.45)] text-white size-9"
          >
            <Icon icon="i-lucide-play" class="size-4" />
          </span>
        </div>
        <div
          v-if="index === PREVIEW_TILE_COUNT && showSummaryOverlay"
          class="acl-image-album__summary-overlay pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-[rgba(0,0,0,0.7)] text-[2rem] font-bold leading-none text-white"
        >
          {{ remainingLabel }}
        </div>
        <div
          v-if="!showSingleTimestamp"
          class="pointer-events-none absolute bottom-1 right-1 z-30 flex items-center gap-0.5 text-[10px] leading-none text-white drop-shadow-md"
        >
          <time>{{ readableTime(item.message) }}</time>
          <MessageStatus
            v-if="statusToShow(item.message)"
            :status="statusToShow(item.message)"
          />
        </div>
      </div>
      <div
        v-if="showSingleTimestamp && albumTimestampMessage"
        class="pointer-events-none absolute bottom-1 right-1 z-30 flex items-center text-[10px] leading-none text-white drop-shadow-md"
      >
        <time>{{ readableTime(albumTimestampMessage) }}</time>
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && galleryAttachment"
    v-model:show="showGallery"
    :attachment="galleryAttachment"
    :all-attachments="albumGalleryAttachments"
    @close="() => (showGallery = false)"
  />
</template>
